import { en } from './en'
import type { TranslationShape } from '../types'

export const de: TranslationShape<typeof en> = {
  common: {
    localeLabel: 'Sprache',
    saveChanges: 'Änderungen speichern',
    cancel: 'Abbrechen',
    close: 'Schließen',
    loading: 'Lädt…',
    none: 'Keine',
    or: 'oder',
    default: 'Standard',
    inactive: 'Inaktiv',
    keySet: 'Schlüssel gesetzt',
    noApiKey: 'Kein API-Schlüssel',
    tokens: 'Token',
    requests: 'Anfragen',
    estCost: 'Geschätzte Kosten',
    yes: 'Ja',
    no: 'Nein',
  },
  locale: {
    english: 'EN',
    german: 'DE',
  },
  status: {
    ACTIVE: 'Aktiv',
    DRAFT: 'Entwurf',
    PAUSED: 'Pausiert',
    ARCHIVED: 'Archiviert',
  },
  landing: {
    hero: {
      badge: 'Plattform für KI-Franchise-Agenturen',
      title: 'White-Label',
      titleAccent: 'KI-Franchises betreiben',
      subtitle: 'Jede Agentur läuft als Franchise-Workspace. Shared Ops steuert Hosting- und Kanal-Zugangsdaten, während Franchises kuratierte KI-Angebote über Telegram, WhatsApp und Telefon ausrollen.',
      ctaPrimary: 'Jetzt starten',
      ctaSecondary: 'Konsole öffnen',
      providerSupport: 'Shared Ops · Railway-Hosting · Telegram · WhatsApp · Twilio Voice',
      scroll: 'scrollen',
    },
    features: {
      eyebrow: 'Franchise-Betrieb',
      title: 'Shared Ops steuern, lokale Franchises skalieren',
      subtitle: 'Ein franchise-orientiertes Betriebsmodell mit zentraler Sicherheit und dezentraler Ausführung.',
      multiProviderTitle: 'Shared Ops Kontrolle',
      multiProviderDescription: 'Zentraler Secret-Vault und Hosting-Anbindungen für alle Franchise-Workspaces.',
      secureTitle: 'Kuratierte KI-Angebote',
      secureDescription: 'Template-Pakete nach Zielgruppe und Branche für Support, Sales, Operations und mehr.',
      analyticsTitle: 'Kanalübergreifende Laufzeit',
      analyticsDescription: 'Einen Agenten über Telegram, WhatsApp und Telefon bereitstellen.',
      chatTitle: 'Rechnungsfähige Nutzung',
      chatDescription: 'Nutzung und Revenue-Share nach Franchise, Kanal und Abrechnungszeitraum verfolgen.',
    },
    cta: {
      title: 'Bereit für Ihr KI-Franchise-Netzwerk?',
      subtitle: 'Franchise-Workspaces erstellen, Kanäle verbinden und den Betrieb zentral über Shared Ops steuern.',
      button: 'Jetzt starten',
      meta: 'Franchise-fähig · Mehrkanal · Offene Architektur',
      builtOn: 'Auf Open Source aufgebaut ·',
    },
  },
  auth: {
    login: {
      title: 'Melden Sie sich in Ihrem Konto an',
      passkey: 'Mit Passkey anmelden',
      passkeyFailed: 'Passkey-Anmeldung fehlgeschlagen',
      passkeyInvalidOptions: 'Passkey-Anmeldung ist vorübergehend nicht verfügbar. Bitte nutzen Sie unten E-Mail und Passwort.',
      useEmail: 'E-Mail und Passwort verwenden',
      emailPlaceholder: 'sie@example.com',
      passwordPlaceholder: 'Passwort',
      signIn: 'Anmelden',
      invalidCredentials: 'Ungültige Zugangsdaten',
      signInFailed: 'Anmeldung fehlgeschlagen',
      noAccount: 'Sie haben noch kein Konto?',
      register: 'Registrieren',
    },
    register: {
      title: 'Erstellen Sie Ihr Konto',
      fullName: 'Vollständiger Name',
      fullNamePlaceholder: 'Max Mustermann',
      email: 'E-Mail',
      emailOptional: 'E-Mail (optional)',
      emailOptionalHint: 'Falls angegeben, nutzen wir sie als Passkey-Name und für spätere E-Mail-Verifizierung.',
      emailPlaceholder: 'sie@example.com',
      password: 'Passwort',
      passwordPlaceholder: 'Mindestens 8 Zeichen',
      organizationName: 'Name des Franchise-Workspaces',
      organizationNamePlaceholder: 'Berlin Agentur Franchise',
      organizationHint: 'Dadurch wird Ihr erster Franchise-Workspace erstellt',
      createAccount: 'Konto erstellen',
      createWithPasskey: 'Mit Passkey erstellen',
      newsletterOptIn: 'Ich möchte den Newsletter per E-Mail erhalten.',
      haveAccount: 'Sie haben bereits ein Konto?',
      signIn: 'Anmelden',
      registrationFailed: 'Registrierung fehlgeschlagen',
      passkeyRequired: 'Ein Passkey ist erforderlich, um fortzufahren',
      passkeyTitle: 'Passkey hinzufügen',
      passkeySubtitle: 'Verwenden Sie die Biometrie Ihres Geräts oder einen Sicherheitsschlüssel für eine sofortige, passwortlose Anmeldung.',
      setupPasskey: 'Passkey einrichten',
      skipForNow: 'Jetzt überspringen',
    },
    verifyBanner: {
      title: 'Verifizieren Sie Ihre E-Mail, wenn Sie bereit sind',
      subtitle: 'Verwenden Sie {email}, um später Verifizierungs- und Recovery-E-Mails zu erhalten.',
      sendAction: 'Verifizierungs-E-Mail senden',
      sent: 'Verifizierungs-E-Mail wurde gesendet.',
      sendFailed: 'Verifizierungs-E-Mail konnte nicht gesendet werden.',
    },
  },
  sidebar: {
    dashboard: 'Dashboard',
    agents: 'Agenten',
    usage: 'Nutzung',
    billing: 'Abrechnung',
    settings: 'Einstellungen',
    signOut: 'Abmelden',
  },
  dashboard: {
    home: {
      title: 'Dashboard',
      subtitle: 'Überblick über Ihre KI-Agenten und Nutzung',
      newAgent: 'Neuer Agent',
      recentAgents: 'Neueste Agenten',
      viewAll: 'Alle anzeigen',
      noAgents: 'Noch keine Agenten',
      createFirstAgent: 'Erstellen Sie Ihren ersten Agenten',
      tokenUsage30d: 'Token-Nutzung (30 Tage)',
    },
    stats: {
      totalTokens: 'Token gesamt',
      last30Days: 'Letzte 30 Tage',
      activeAgents: 'Aktive Agenten',
      runningNow: 'Aktuell laufend',
      requests: 'Anfragen',
    },
  },
  agents: {
    list: {
      title: 'Agenten',
      agentCountSingular: '{count} Agent',
      agentCountPlural: '{count} Agenten',
      newAgent: 'Neuer Agent',
      newAgentTitle: 'Neuer Agent',
      nameLabel: 'Name',
      namePlaceholder: 'Kundensupport-Agent',
      descriptionLabel: 'Beschreibung',
      descriptionPlaceholder: 'Was macht dieser Agent?',
      systemPromptLabel: 'System-Prompt',
      systemPromptPlaceholder: 'Sie sind ein hilfreicher Assistent...',
      providerLabel: 'KI-Anbieter',
      selectProvider: 'Anbieter auswählen…',
      offeringPackLabel: 'Kuratierungspaket',
      offeringPackPlaceholder: 'Paket auswählen…',
      createAgent: 'Agent erstellen',
      noAgents: 'Noch keine Agenten',
      createFirstAgent: 'Erstellen Sie Ihren ersten Agenten',
    },
    detail: {
      activate: 'Aktivieren',
      pause: 'Pausieren',
      tabChat: 'Chat',
      tabSettings: 'Einstellungen',
      statusLabel: 'Agent ist {status}',
      activateToChat: 'Aktivieren Sie den Agenten, um den Chat zu starten',
      settingsTitle: 'Agenten-Einstellungen',
      nameLabel: 'Name',
      descriptionLabel: 'Beschreibung',
      systemPromptLabel: 'System-Prompt',
      systemPromptHint: 'Anweisungen, die Persönlichkeit und Verhalten des Agenten definieren',
      providerLabel: 'KI-Anbieter',
      noneOption: 'Keine',
      saveChanges: 'Änderungen speichern',
      chatButton: 'Chat',
      offeringPackTitle: 'Kuratiertes Angebot',
      offeringPackPlaceholder: 'Paket auswählen…',
      applyOfferingPack: 'Paket anwenden',
      channelBindingsTitle: 'Kanal-Bindings',
      channelBindingsPlaceholder: 'Endpoint auswählen…',
      bindChannelButton: 'Kanal verbinden',
      noChannelBindings: 'Noch keine Kanal-Bindings',
      defaultBinding: 'Standard',
      removeBinding: 'Entfernen',
    },
  },
  chat: {
    startConversationWith: 'Starten Sie eine Unterhaltung mit {agentName}',
    messagePlaceholder: 'Nachricht an {agentName}… (Enter zum Senden, Shift+Enter für neue Zeile)',
    genericError: 'Unbekannter Fehler',
    errorPrefix: 'Fehler',
  },
  settings: {
    index: {
      title: 'Einstellungen',
      subtitle: 'Konfigurieren Sie Franchise-Workspace und Shared Ops',
      modelProvidersTitle: 'Modellanbieter',
      modelProvidersDescription: 'Konfigurieren Sie KI-Modellanbieter und API-Schlüssel',
      organizationTitle: 'Organisation',
      organizationDescription: 'Verwalten Sie Identität, Branche und Branding des aktiven Workspaces',
      workspacesTitle: 'Workspaces',
      workspacesDescription: 'Franchise-Workspaces erstellen, Kontext wechseln und Custom Domains verwalten',
      channelsTitle: 'Kanal-Endpunkte',
      channelsDescription: 'Telegram-, WhatsApp- und Telefon-Endpunkte aus Shared-Ops-Profilen erstellen',
      opsSecretsTitle: 'Shared Ops Secrets',
      opsSecretsDescription: 'Verschlüsselte Zugangsdaten für Kanäle und Hosting-Anbieter verwalten',
      opsHostingTitle: 'Shared Ops Hosting',
      opsHostingDescription: 'Railway-Verbindungen verwalten und Deployment-Status synchronisieren',
      opsChannelsTitle: 'Shared Ops Kanalprofile',
      opsChannelsDescription: 'Wiederverwendbare Kanalprofile für Telegram, WhatsApp und Telefon veröffentlichen',
    },
    providers: {
      title: 'Modellanbieter',
      subtitle: 'Konfigurieren Sie Anbieter-Zugangsdaten für Ihre Agenten',
      addProvider: 'Anbieter hinzufügen',
      addProviderTitle: 'Anbieter hinzufügen',
      displayName: 'Anzeigename',
      displayNamePlaceholder: 'Meine OpenAI-Konfiguration',
      providerLabel: 'Anbieter',
      selectProvider: 'Anbieter auswählen…',
      modelLabel: 'Modell',
      selectModel: 'Modell auswählen…',
      apiKeyLabel: 'API-Schlüssel',
      apiKeyPlaceholder: 'sk-…',
      apiKeyHint: 'Verschlüsselt gespeichert',
      baseUrlLabel: 'Basis-URL',
      baseUrlPlaceholder: 'http://localhost:11434',
      setDefault: 'Als Standardanbieter festlegen',
      saveProvider: 'Anbieter speichern',
      noProviders: 'Keine Anbieter konfiguriert',
      addProviderToEnable: 'Fügen Sie einen Anbieter hinzu, um KI-Agenten zu aktivieren',
    },
    organization: {
      title: 'Organisation',
      subtitle: 'Verwalten Sie die Einstellungen des aktiven Workspaces',
      notMember: 'Sie sind derzeit keiner Organisation zugeordnet.',
      organizationName: 'Name der Organisation',
      organizationNamePlaceholder: 'Musterfirma GmbH',
      createOrganization: 'Organisation erstellen',
      sectorLabel: 'Branche',
      saved: 'Gespeichert!',
      organizationId: 'Organisations-ID',
      plan: 'Plan',
      sectors: {
        general: 'Allgemein',
        healthcare: 'Gesundheitswesen',
        finance: 'Finanzen',
        legal: 'Recht',
        logistics: 'Logistik',
        retail: 'Einzelhandel',
        education: 'Bildung',
        manufacturing: 'Fertigung',
      },
    },
    workspaces: {
      title: 'Franchise-Workspaces',
      subtitle: 'Agentur-als-Franchise-Workspaces und Shared-Ops-Kontext verwalten',
      createTitle: 'Franchise-Workspace erstellen',
      nameLabel: 'Workspace-Name',
      namePlaceholder: 'Berlin Agentur Franchise',
      sectorLabel: 'Branche',
      sectorPlaceholder: 'Einzelhandel',
      createButton: 'Workspace erstellen',
      active: 'Aktiv',
      switchButton: 'Wechseln',
      customDomainLabel: 'Custom Domain',
      customDomainPlaceholder: 'ai.ihr-franchise.de',
      themeAccentLabel: 'Theme-Akzentfarbe',
      themeAccentPlaceholder: '#0ea5e9',
      saveWorkspace: 'Workspace speichern',
    },
    channels: {
      title: 'Kanal-Endpunkte',
      subtitle: 'Franchise-Endpunkte aus Shared-Ops-Kanalprofilen instanziieren',
      createTitle: 'Endpoint erstellen',
      nameLabel: 'Endpoint-Name',
      namePlaceholder: 'WhatsApp Eingang',
      typeLabel: 'Kanaltyp',
      profileLabel: 'Shared Ops Profil',
      profilePlaceholder: 'Kanalprofil auswählen…',
      configLabel: 'Endpoint-Konfiguration (JSON)',
      createButton: 'Endpoint erstellen',
      invalidJson: 'Konfiguration muss gültiges JSON sein',
    },
    opsSecrets: {
      title: 'Shared Ops Secrets',
      subtitle: 'Verschlüsselter Credential-Vault für Kanäle und Hosting',
      createTitle: 'Secret hinzufügen',
      nameLabel: 'Secret-Name',
      namePlaceholder: 'WhatsApp Access Token',
      providerLabel: 'Provider-Schlüssel',
      providerPlaceholder: 'WHATSAPP_ACCESS_TOKEN',
      valueLabel: 'Secret-Wert',
      valuePlaceholder: 'Secret-Wert einfügen',
      createButton: 'Secret speichern',
      disableButton: 'Deaktivieren',
    },
    opsHosting: {
      title: 'Shared Ops Hosting',
      subtitle: 'Railway-basierte Hosting-Verbindungen für Franchise-Deployments verwalten',
      createTitle: 'Hosting-Verbindung hinzufügen',
      nameLabel: 'Verbindungsname',
      namePlaceholder: 'Railway Produktion',
      projectLabel: 'Railway Projekt-ID',
      projectPlaceholder: 'railway-project-id',
      environmentLabel: 'Railway Environment-ID',
      environmentPlaceholder: 'railway-environment-id',
      secretLabel: 'Credential Secret',
      secretPlaceholder: 'Secret auswählen…',
      createButton: 'Verbindung erstellen',
      syncButton: 'Synchronisieren',
    },
    opsChannels: {
      title: 'Shared Ops Kanalprofile',
      subtitle: 'Wiederverwendbare Laufzeitprofile für Telegram, WhatsApp und Telefon erstellen',
      createTitle: 'Kanalprofil erstellen',
      nameLabel: 'Profilname',
      namePlaceholder: 'Globales WhatsApp-Profil',
      typeLabel: 'Kanaltyp',
      secretLabel: 'Credential Secret',
      secretPlaceholder: 'Secret auswählen…',
      configLabel: 'Profilkonfiguration (JSON)',
      createButton: 'Profil erstellen',
      invalidJson: 'Konfiguration muss gültiges JSON sein',
    },
  },
  usage: {
    title: 'Nutzungsanalyse',
    subtitle: 'Letzte 30 Tage',
    totalTokens: 'Token gesamt',
    totalRequests: 'Anfragen gesamt',
    estCost: 'Geschätzte Kosten',
    dailyTokenUsage: 'Tägliche Token-Nutzung',
    byProvider: 'Nach Anbieter',
    providerLine: '{requests} Anfragen · {cost}',
    loading: 'Nutzungsdaten werden geladen…',
    noUsage: 'Noch keine Nutzungsdaten',
  },
  billing: {
    title: 'Abrechnung und Revenue Share',
    subtitle: 'Rechnungsfähige Nutzungsübersicht nach Workspace und Kanal',
    generateTitle: 'Abrechnungszeitraum erstellen',
    periodStart: 'Zeitraum Start',
    periodEnd: 'Zeitraum Ende',
    revenueSharePct: 'Revenue Share (%)',
    generateButton: 'Rechnung erzeugen',
    noInvoices: 'Noch keine Rechnungen erzeugt',
    totalTokens: 'Token gesamt',
    totalRequests: 'Anfragen gesamt',
    totalCost: 'Gesamtkosten',
    revenueShare: 'Revenue Share',
  },
}
